import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Icon, TextPassage, Fieldset, Table, Section, Alert } from '../common';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import { ResourceGroupEntry, EXPIRATION_OPTIONS } from './components/AddResourceGroupModal';
import './PolicyExemptionSubmit.css';
import RiskBadge from './components/RiskBadge';
import PolicyExemptionRequestService from '../../services/PolicyExemptionRequestService';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { ExemptionDetails } from './ExemptionDetailsForm';
import ExemptionDetailsFormWrapper from '../common/wrappers/ExemptionDetailsFormWrapper';
import ReadOnlyField from '../common/form-controls/ReadOnly';
import { CATEGORIES } from './components/ExemptionCategoryInput';

export interface PolicyExemptionSubmitProps {
	selectedSubscriptions: SubscriptionRequest[];
	entriesBySubscription: Record<number, ResourceGroupEntry[]>;
	bulkExpirationBySubscription: Record<number, string>;
	selectedAssignmentBySubscription: Record<number, any>;
	selectedPoliciesBySubscription: Record<number, any[]>;
	exemptionDetails?: ExemptionDetails;
	onBack: () => void;
}

const PolicyExemptionSubmit = ({
	selectedSubscriptions = [],
	entriesBySubscription = {},
	bulkExpirationBySubscription = {},
	selectedAssignmentBySubscription = {},
	selectedPoliciesBySubscription = {},
	exemptionDetails,
	onBack,
}: PolicyExemptionSubmitProps) => {
	const navigate = useNavigate();
	const policyExemptionRequestsService = new PolicyExemptionRequestService();
	const [submissionAlert, setSubmissionAlert] = useState<AlertBanner>({ visible: false });
	const [submitting, setSubmitting] = useState<boolean>(false);

	const handleBack = () => {
		onBack();
	};

	const handleSubmit = async () => {
		window.scroll(0, 0);
		setSubmitting(true);
		setSubmissionAlert({
			visible: true,
			text: 'Submitting request...',
			variant: 'default',
		});
		try {
			const payload = selectedSubscriptions.flatMap((subscription) => {
				const subscriptionId = subscription.id;
				const entries = entriesBySubscription[subscriptionId] ?? [];
				const assignment = selectedAssignmentBySubscription[subscriptionId];
				const selectedPolicies = selectedPoliciesBySubscription[subscriptionId] ?? [];
				if (entries.length === 0) {
					const bulkValue = bulkExpirationBySubscription[subscriptionId];
					const bulkLabel = EXPIRATION_OPTIONS.find((o) => o.value === bulkValue)?.text ?? '';
					const durationRequested = bulkLabel.toLowerCase().startsWith('temporary')
						? 'temporary'
						: bulkLabel.replace(/\s+/g, '');
					return [
						{
							displayName: exemptionDetails?.exceptionDescription,
							assignmentId: assignment?.id,
							durationRequested: durationRequested || null,
							riskAssessmentLink: exemptionDetails?.link ?? '',
							businessJustification: exemptionDetails?.justification,
							resource: null,
							resourceGroup: null,
							subscriptionRequestId: subscriptionId,
							policyDefinitionsIds: selectedPolicies.map((policy) => policy.id),
						},
					];
				}
				return entries.map((entry) => ({
					displayName: exemptionDetails?.exceptionDescription,
					assignmentId: assignment?.id,
					durationRequested: entry.expiration.toLowerCase().startsWith('temporary')
						? 'temporary'
						: entry.expiration.replace(/\s+/g, ''),
					riskAssessmentLink: exemptionDetails?.link ?? '',
					businessJustification: exemptionDetails?.justification,
					resource: entry.resourceId ?? null,
					resourceGroup: entry.resourceGroupId ?? null,
					subscriptionRequestId: subscriptionId,
					policyDefinitionsIds: selectedPolicies.map((policy) => policy.id),
				}));
			});
			const response = await policyExemptionRequestsService.create(payload);
			setSubmissionAlert({
				visible: true,
				variant: 'success',
				text: 'Successfully Submitted',
			});
			selectedSubscriptions.forEach((subscription) => {
				window.open(`/subscriptions/${subscription.id}/policyExemptionRequests`, '_blank');
			});
			setTimeout(() => {
				navigate('/subscriptions');
			}, 5000);
		} catch (err: any) {
			console.error(err);
			const responseData = err?.response?.data;
			const responseDataObj = responseData as
				| {
						title?: string;
						message?: string;
						errors?: Record<string, string[]>;
				  }
				| undefined;
			let errorText =
				responseDataObj?.title ||
				responseDataObj?.message ||
				(typeof responseData === 'string'
					? responseData
					: 'An error occurred with your request. Please try again later.');
			if (responseDataObj?.errors) {
				const details = Object.entries(responseDataObj.errors)
					.flatMap(([field, messages]) => messages.map((msg) => `${field}: ${msg}`))
					.join('\n');
				errorText = `${errorText}\n${details}`;
			}
			setSubmissionAlert({
				visible: true,
				variant: 'error',
				text: errorText,
			});
		} finally {
			setSubmitting(false);
		}
	};

	const getSelectedAssignment = (subId: number) => {
		return selectedAssignmentBySubscription[subId] ?? null;
	};

	const handleAlertClose = () => {
		setSubmissionAlert({ visible: false });
	};

	return (
		<>
			{submissionAlert.visible && (
				<Alert
					variant={submissionAlert.variant}
					body={submissionAlert.text}
					hasButton
					onClose={handleAlertClose}
				/>
			)}
			<TextPassage>
				<p>
					Review all information before submitting. Once submitted, the request will be routed for approval.
				</p>
			</TextPassage>

			{exemptionDetails && (
				<ExemptionDetailsFormWrapper title="Exemption Details">
					<ReadOnlyField label="Exception Description" text={exemptionDetails.exceptionDescription || '—'} />
					<ReadOnlyField label="Category" text={exemptionDetails.category ? CATEGORIES.find((c) => c.value === exemptionDetails.category)?.text : '—'} />
					<ReadOnlyField label="Risk Assessment Link" text={exemptionDetails.link || '—'} />
					<ReadOnlyField label="Business Justification" text={exemptionDetails.justification || '—'} />
				</ExemptionDetailsFormWrapper>
			)}

			{selectedSubscriptions.length === 0 ? (
				<TextPassage>
					<p>No subscriptions selected. Please go back and select at least one subscription.</p>
				</TextPassage>
			) : (
				selectedSubscriptions.map((sub) => {
					const entries = entriesBySubscription[sub.id] ?? [];
					const assignment = getSelectedAssignment(sub.id);

					return (
						<Section
							key={sub.id}
							title={sub.createdSubscriptionName ?? sub.createdSubscriptionId ?? `Subscription ${sub.id}`}
							type="em-c-section--expandable"
							marginNone
                            titleFontSize="large-2"
                            titleMarginBottom="sm"
						>
							{/* Resource Groups & Resources */}
							<Fieldset legend="Resource Groups &amp; Resources">
								{entries.length === 0 ? (
									<TextPassage>
										<p>
											No RG / Resources selected — policy will be applied at subscription level.
										</p>
										<p>
											<strong>Expiration:</strong>{' '}
											{EXPIRATION_OPTIONS.find((o) => o.value === bulkExpirationBySubscription[sub.id])?.text ?? '—'}
										</p>
									</TextPassage>
								) : (
									<Table>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell>Resource Group</Table.Head.Cell>
												<Table.Head.Cell>Resource</Table.Head.Cell>
												<Table.Head.Cell>Expiration</Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>
											{entries.map((entry, i) => (
												<Table.Row key={i}>
													<Table.Cell>{entry.resourceGroupName || '—'}</Table.Cell>
													<Table.Cell>{entry.resourceName || '—'}</Table.Cell>
													<Table.Cell>{entry.expiration || '—'}</Table.Cell>
												</Table.Row>
											))}
										</Table.Body>
									</Table>
								)}
							</Fieldset>

							{/* Selected Assignment */}
							<Fieldset legend="Assignment">
								{!assignment ? (
									<TextPassage>
										<p>No assignment selected.</p>
									</TextPassage>
								) : (
									<Table>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell>Display Name</Table.Head.Cell>
												<Table.Head.Cell>Description</Table.Head.Cell>
												<Table.Head.Cell>Scope</Table.Head.Cell>
												<Table.Head.Cell>Policies</Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>
											<Table.Row>
												<Table.Cell>{assignment.properties?.displayName ?? '—'}</Table.Cell>
												<Table.Cell>{assignment.properties?.description ?? '—'}</Table.Cell>
												<Table.Cell>
													{assignment.properties?.scopeDisplayName ?? '—'}
												</Table.Cell>
												<Table.Cell>
													{assignment.properties?.policyDefinitions?.length ?? '—'}
												</Table.Cell>
											</Table.Row>
										</Table.Body>
									</Table>
								)}
							</Fieldset>

							{/* Policies */}
							<Fieldset legend="Policies">
								{(selectedPoliciesBySubscription[sub.id] ?? []).length === 0 ? (
									<TextPassage>
										<p>No policies selected.</p>
									</TextPassage>
								) : (
									<Table>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell>Display Name</Table.Head.Cell>
												<Table.Head.Cell>Category</Table.Head.Cell>
												<Table.Head.Cell>Risk Rating</Table.Head.Cell>
												<Table.Head.Cell>Description</Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>
											{(selectedPoliciesBySubscription[sub.id] ?? []).map((policy) => (
												<Table.Row key={policy.id}>
													<Table.Cell>{policy.properties?.displayName ?? '—'}</Table.Cell>
													<Table.Cell>
														{policy.properties?.metadata?.category ?? '—'}
													</Table.Cell>
													<Table.Cell>
														<RiskBadge risk={policy.properties?.metadata?.xomRiskRating} />
													</Table.Cell>
													<Table.Cell>{policy.properties?.description ?? '—'}</Table.Cell>
												</Table.Row>
											))}
										</Table.Body>
									</Table>
								)}
							</Fieldset>
						</Section>
					);
				})
			)}

			<br />
			<ButtonGroup>
				<Button variant="secondary" size="small" onClick={handleBack}>
					<Icon icon="chevron-left" size="small" />
					<span>Back</span>
				</Button>
				<Button
					variant="primary"
					size="small"
					disabled={selectedSubscriptions.length === 0}
					onClick={handleSubmit}
				>
					<span>Submit</span>
				</Button>
			</ButtonGroup>
		</>
	);
};

export default PolicyExemptionSubmit;
